// The review chain on one deliverable: who is asked in what order, where it is
// now, the history, and the separate pilot question that never blocks the chain.

import React, { useState } from 'react';
import { useToast } from '../components/Toast/ToastContext';
import { askPilot, requestReview, withdrawReview } from './sparkApi';
import type { Person, ProductionDetail, Review, Task } from './sparkApi';
import { ReviewRequestCard } from './SparkBits';
import type { ReviewStep } from './sparkRules';
import { firstName } from './sparkRules';
import { errorMessage } from './sparkUi';
import { formatTimestamp } from '../utils/date-utils';
import { Alert } from '../components/ui/Alert';
import Appear from '../components/ui/Appear';
import { Button } from '../components/ui/Button';
import { TextArea } from '../components/ui/Field';
import Reveal from '../components/ui/Reveal';
import Tooltip from '../components/ui/Tooltip';
import { IconCheck } from '../components/ui/icons';

let seq = 0;

type StepState = 'pass' | 'now' | 'changes' | 'next';

/** The round mark on the chain: a check that draws itself when the step passes, a lit dot while it waits. */
const StepNode: React.FC<{ state: StepState }> = ({ state }) => {
  if (state === 'pass') {
    return (
      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-green-400/15 text-green-300">
        <IconCheck className="h-3.5 w-3.5" draw />
      </span>
    );
  }
  if (state === 'now') {
    return (
      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/15 shadow-glow">
        <span className="h-2 w-2 rounded-full bg-primary drop-shadow-glow" />
      </span>
    );
  }
  if (state === 'changes') {
    return (
      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-orange-400/15 text-orange-300">
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5">
          <rect x="10.5" y="4" width="3" height="11" rx="1.5" fill="currentColor" />
          <circle cx="12" cy="19" r="1.75" fill="currentColor" />
        </svg>
      </span>
    );
  }
  return (
    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-surface-container-highest">
      <span className="h-2 w-2 rounded-full bg-outline-variant" />
    </span>
  );
};

const STEP_NAME: Record<ReviewStep, string> = {
  designer: 'Designer', director: 'Director', producer: 'Producer', client: 'Client', pilot: 'Pilot',
};

const AskControl: React.FC<{
  openLabel: string;
  sendLabel: string;
  fieldLabel: string;
  tonal?: boolean;
  onSend: (ask: string) => Promise<void>;
}> = ({ openLabel, sendLabel, fieldLabel, tonal, onSend }) => {
  const [open, setOpen] = useState(false);
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState(false);
  const [fieldId] = useState(() => `sp-ask-${++seq}`);

  const send = async () => {
    setBusy(true);
    try {
      await onSend(ask.trim());
      setOpen(false);
      setAsk('');
    } catch {
      /* the caller toasts; keep the field so nothing typed is lost */
    }
    setBusy(false);
  };
  return (
    <>
      <Reveal show={!open}>
        <Button variant={tonal ? 'tonal' : 'filled'} size="sm" onClick={() => setOpen(true)}>{openLabel}</Button>
      </Reveal>
      <Reveal show={open}>
        <div className="w-full min-w-[16rem] p-0.5">
          <label htmlFor={fieldId} className="block text-[13px] font-medium text-on-surface">{fieldLabel}</label>
          <TextArea
            id={fieldId}
            autoFocus
            rows={2}
            value={ask}
            maxLength={2000}
            onChange={(e) => setAsk(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); if (e.key === 'Escape') setOpen(false); }}
            placeholder="Optional. Say what to look at."
            className="mt-1"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button variant="filled" size="sm" disabled={busy} onClick={send}>{sendLabel}</Button>
            <Button variant="quiet" size="sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      </Reveal>
    </>
  );
};

const SparkReviewPanel: React.FC<{
  task: Task;
  detail: ProductionDetail;
  people: Person[];
  studio: boolean;
  approver: boolean;
  archived: boolean;
  userId: string;
  onChanged: () => void;
}> = ({ task, detail, people, studio, approver, archived, userId, onChanged }) => {
  const toast = useToast();
  const mine = detail.reviews.filter((r) => r.task_id === task.id);
  const chainRows = mine.filter((r) => r.kind === 'chain');
  const pilotRows = mine.filter((r) => r.kind === 'pilot').sort((a, b) => b.requested_at.localeCompare(a.requested_at));
  const round = chainRows.reduce((m, r) => Math.max(m, r.round), 0);
  const latest = chainRows.filter((r) => r.round === round);
  const waiting = latest.find((r) => r.status === 'waiting') || null;
  const changed = !waiting ? latest.find((r) => r.status === 'changes') || null : null;
  const pilotWaiting = pilotRows.find((r) => r.status === 'waiting') || null;
  const pilotLast = pilotRows[0] || null;
  const pilotMember = detail.team.find((m) => m.role === 'pilot');
  const producerMember = detail.team.find((m) => m.role === 'producer');

  const myRoles = people.find((p) => p.id === userId)?.roles || [];
  // Mirrors mayAnswer in api/src/spark/spark-reviews.js.
  const canAnswer = (r: Review) =>
    approver || r.reviewer_id === userId
    || (r.reviewer_id === null && r.step === 'director' && myRoles.includes('director'));

  const nameOf = (id: string | null, fallback: string) =>
    (id && (detail.team.find((m) => m.user_id === id)?.name || people.find((p) => p.id === id)?.name
      || mine.find((r) => r.reviewer_id === id)?.reviewer_name)) || fallback;

  const chain = detail.chain;
  const steps = 'steps' in chain ? chain.steps : [];
  const stateOf = (step: ReviewStep): StepState => {
    const r = latest.find((x) => x.step === step);
    if (!r) return 'next';
    if (r.status === 'passed') return 'pass';
    if (r.status === 'waiting') return 'now';
    if (r.status === 'changes') return 'changes';
    return 'next';
  };
  const allPassed = steps.length > 0 && steps.every((s) => stateOf(s.step) === 'pass');

  const run = async (fn: () => Promise<unknown>, fail: string, ok?: string) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, fail));
      throw e;
    }
  };

  const history = [...mine].filter((r) => r.status !== 'waiting').sort((a, b) => a.requested_at.localeCompare(b.requested_at));
  const closed = task.status === 'closed';
  const errorText = 'error' in chain
    ? chain.error === 'no_designer'
      ? 'This project has no designer on its team, so nobody can review first. Add a designer to the show team, then send it for review.'
      : 'Nobody holds the Director role, so the review cannot continue. Give someone the Director role in the Studio, then send it for review.'
    : null;

  return (
    <section aria-label={`Review of ${task.name}`} className="rounded-control bg-surface-container-low p-3">
      <h3 className="m-0 text-[13px] font-semibold leading-[18px] text-on-surface">Review order</h3>

      {errorText ? (
        <Alert tone="error" className="mt-2">{errorText}</Alert>
      ) : (
        <ol
          aria-label="Review order"
          className="m-0 mt-2 grid list-none grid-cols-1 gap-1 p-0 sm:[grid-template-columns:repeat(var(--steps),minmax(0,1fr))]"
          style={{ '--steps': Math.max(steps.length, 1) } as React.CSSProperties}
        >
          {steps.map((s, i) => {
            const st = stateOf(s.step);
            const who = s.step === 'client' ? 'Client' : nameOf(s.reviewer_id, STEP_NAME[s.step]);
            const sub = s.step === 'client'
              ? `${firstName(nameOf(s.reviewer_id, producerMember?.name || 'The producer'))} records it`
              : s.step === 'designer' && s.merged ? 'Designer and director' : STEP_NAME[s.step];
            const word = st === 'pass' ? 'Passed' : st === 'now' ? 'Waiting now' : st === 'changes' ? 'Changes asked' : 'Next';
            const last = i === steps.length - 1;
            return (
              <li
                key={s.step}
                className={`flex min-w-0 items-start gap-2 rounded-control p-2 transition-[background-color,box-shadow] duration-standard ease-emphasized sm:block ${
                  st === 'now' ? 'bg-primary/10 shadow-glow-soft' : ''
                }`}
              >
                <div className="flex flex-none items-center sm:mb-1.5">
                  <StepNode state={st} />
                  {!last && (
                    <span aria-hidden="true" className="ml-2 hidden h-0.5 flex-1 overflow-hidden rounded-inner bg-outline-variant sm:block">
                      <span
                        className="block h-full origin-left bg-green-300/70 transition-transform duration-spring ease-spring motion-reduce:transition-none"
                        style={{ transform: `scaleX(${st === 'pass' ? 1 : 0})` }}
                      />
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <span className={`block text-xs font-medium ${st === 'pass' ? 'text-green-300' : st === 'now' ? 'text-primary' : st === 'changes' ? 'text-orange-300' : 'text-on-surface-variant'}`}>
                    {word}
                  </span>
                  <span className="block truncate text-sm font-semibold text-on-surface">{who}</span>
                  <span className="block truncate text-xs text-on-surface-variant">{sub}</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <Reveal show={!errorText && allPassed}>
        <p className="m-0 mt-2 flex items-center gap-1.5 text-sm text-green-300"><IconCheck className="h-3.5 w-3.5" />Every reviewer passed it.</p>
      </Reveal>

      <Reveal show={!!waiting}>
        {waiting && (
          <div className="mt-3">
            <p className="m-0 text-sm text-on-surface">
              Waiting on {nameOf(waiting.reviewer_id, STEP_NAME[waiting.step])}, since {formatTimestamp(waiting.requested_at, 'MMM D')}.
              {waiting.ask ? ` Asked: ${waiting.ask}` : ''}
            </p>
            {canAnswer(waiting) && (
              <div className="mt-2">
                {waiting.reviewer_id !== userId && (
                  <p className="m-0 mb-1.5 text-xs font-medium text-on-surface-variant">Answer for {nameOf(waiting.reviewer_id, STEP_NAME[waiting.step])}</p>
                )}
                <ReviewRequestCard
                  review={{
                    id: waiting.id, kind: 'chain', step: waiting.step, merged: waiting.merged, ask: waiting.ask,
                    task_name: task.name, production_name: detail.production.name, production_id: detail.production.id,
                  }}
                  onDone={onChanged}
                />
              </div>
            )}
          </div>
        )}
      </Reveal>

      <Reveal show={!!changed}>
        {changed && (
          <p className="m-0 mt-3 text-sm text-on-surface">
            {changed.decided_by_name || nameOf(changed.reviewer_id, STEP_NAME[changed.step])} asked for changes
            {changed.note ? `: “${changed.note}”` : '.'} Fix it, then send it back for review.
          </p>
        )}
      </Reveal>

      <div className="mt-3 flex flex-wrap items-start gap-2">
        <Appear as="div" show={studio && !closed && !archived && !errorText && !waiting && !allPassed}>
          <AskControl
            openLabel={changed ? 'Send back for review' : 'Send for review'}
            sendLabel="Send for review"
            fieldLabel="What should the first reviewer look at?"
            onSend={(ask) => run(() => requestReview(task.id, ask || undefined), 'Could not send it for review. Try again.', 'Sent for review.')}
          />
        </Appear>
        <Appear as="div" show={!!(studio && waiting && (approver || waiting.requested_by === userId))}>
          <Tooltip label="Withdraw this review" note="Send it again when it is ready.">
            <Button
              size="sm"
              onClick={() => { if (waiting) run(() => withdrawReview(waiting.id), 'Could not start over. Try again.', 'Review withdrawn. Send it again when it is ready.').catch(() => undefined); }}
            >
              Start over
            </Button>
          </Tooltip>
        </Appear>
        <Appear as="div" show={studio && !closed && !archived && !pilotWaiting}>
          <AskControl
            tonal
            openLabel="Ask the pilot"
            sendLabel="Ask the pilot"
            fieldLabel={`What do you want ${firstName(pilotMember?.name) || 'the pilot'} to check?`}
            onSend={(ask) => run(() => askPilot(task.id, ask || undefined), 'Could not ask the pilot. Try again.', 'The pilot has been asked.')}
          />
        </Appear>
      </div>
      <p className="m-0 mt-2 max-w-[65ch] text-xs text-on-surface-variant">
        {pilotWaiting
          ? `Asked ${nameOf(pilotWaiting.reviewer_id, 'the pilot')}. The review order does not wait for the answer.`
          : pilotLast && pilotLast.status !== 'withdrawn'
            ? `${nameOf(pilotLast.reviewer_id, 'The pilot')} answered${pilotLast.status === 'changes' ? ' with changes' : ', looks good'}${pilotLast.note ? `: “${pilotLast.note}”` : '.'}`
            : 'The pilot is not a step in the review order. Ask when you want eyes from the field. Nothing waits on the answer.'}
      </p>
      <Reveal show={!!pilotWaiting && canAnswer(pilotWaiting)}>
        {pilotWaiting && (
          <div className="mt-2">
            {pilotWaiting.reviewer_id !== userId && (
              <p className="m-0 mb-1.5 text-xs font-medium text-on-surface-variant">Answer for {nameOf(pilotWaiting.reviewer_id, 'the pilot')}</p>
            )}
            <ReviewRequestCard
              review={{
                id: pilotWaiting.id, kind: 'pilot', step: 'pilot', merged: false, ask: pilotWaiting.ask,
                task_name: task.name, production_name: detail.production.name, production_id: detail.production.id,
              }}
              onDone={onChanged}
            />
          </div>
        )}
      </Reveal>

      <Reveal show={history.length > 0}>
        <ul aria-label="Review history" className="m-0 mt-3 list-none space-y-1 p-0 text-xs text-on-surface-variant">
          {history.map((r: Review) => (
            <li key={r.id}>
              {r.kind === 'pilot' ? 'Pilot: ' : `${STEP_NAME[r.step]}: `}
              {r.status === 'withdrawn'
                ? 'withdrawn'
                : `${r.decided_by_name || r.reviewer_name || 'Someone'} ${r.status === 'passed' ? 'passed it' : `asked for changes${r.note ? `: “${r.note}”` : ''}`}`}
              {r.decided_at ? `, ${formatTimestamp(r.decided_at, 'MMM D')}` : ''}
              {r.via === 'slack' ? ' (on Slack)' : ''}
            </li>
          ))}
        </ul>
      </Reveal>
    </section>
  );
};

export default SparkReviewPanel;
